import { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/solid";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import FormattedDate from "../../components/FormattedDate";
import AddManagerDialog from "./AddManagerDialog";
import PasswordConfirmDialog, { submitAndReset } from "./PasswordConfirmDialog";
import RecentChangesTable from "./RecentChangesTable";
import {
	SecretAuditAction,
	SecretManagerViewModel,
	SecretsStatusViewModel,
	useSetSecretAccessMutation,
} from "./secretsApiSlice";

interface Props {
	data: SecretsStatusViewModel;
}

/**
 * The people who can manage secrets. Every Super Administrator can see it; only people who can
 * manage access (or anyone, while nobody can manage secrets yet) can add or revoke.
 */
export default function ManagersTab({ data }: Props) {
	const [setSecretAccess, { reset }] = useSetSecretAccessMutation();
	const [isAddManagerFormOpen, setIsAddManagerFormOpen] = useState(false);
	const [isRevokingFormOpen, setIsRevokingFormOpen] = useState<SecretManagerViewModel>();

	const nameBodyTemplate = (manager: SecretManagerViewModel) => (
		<div>
			<div className="font-medium">
				{manager.fullName}
				{manager.isYou && <span className="font-normal text-gray-500"> (you)</span>}
			</div>
			<div className="text-sm text-gray-600">{manager.userName}</div>
		</div>
	);

	const grantedBodyTemplate = (manager: SecretManagerViewModel) =>
		manager.accessGrantedAtUtc ? (
			<FormattedDate date={manager.accessGrantedAtUtc} />
		) : (
			<span className="text-gray-500">—</span>
		);

	const revokeBodyTemplate = (manager: SecretManagerViewModel) => (
		<div className="flex justify-end">
			<Button
				id={`revoke-${manager.id}-button`}
				className="btn btn-neutral h-fit w-fit whitespace-nowrap border border-red-200 !bg-red-100 !text-red-700 hover:!bg-red-200"
				disabled={manager.isYou}
				title={manager.isYou ? "You can't change your own access" : undefined}
				onClick={() => setIsRevokingFormOpen(manager)}>
				Revoke
			</Button>
		</div>
	);

	return (
		<div className="pt-4">
			{data.nobodyCanManageSecrets && (
				<AlertMessage
					className="mb-4"
					type={AlertLevel.Warning}
					message="Nobody can manage secrets yet. To get started, add another Super Administrator. You cannot add yourself."
					showIcon
				/>
			)}

			{data.canManageAccess ? (
				<p className="text-sm text-gray-600 mb-2">
					You may grant other super administrators permission to manage secrets using the
					Add Manager button and revoke their access. However, you cannot grant or revoke
					access for yourself and must ask another manager for assistance.
				</p>
			) : (
				<p className="text-sm text-gray-600 mb-2">
					Only the managers below can update secrets and grant access to manage the
					secrets. To gain access, you must ask a manager below for assistance.
				</p>
			)}

			<div className="flex justify-between items-end w-full">
				{!data.canManageSecrets && <h2>Managers</h2>}
				{data.canManageAccess && (
					<div className="flex justify-end ml-auto">
						<Button
							id="add-secrets-manager-button"
							className="btn btn-primary h-fit w-fit whitespace-nowrap"
							onClick={() => setIsAddManagerFormOpen(true)}>
							<span className="flex items-center">
								<PlusIcon className="size-4 mr-2" /> Add Manager
							</span>
						</Button>
					</div>
				)}
			</div>
			<DataTable
				value={data.managers ?? []}
				dataKey="id"
				emptyMessage="No managers found"
				stripedRows
				className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
				style={{ paddingTop: "16px" }}>
				<Column header="Name" body={nameBodyTemplate} />
				<Column header="Email" field="email" />
				<Column header="Access Granted" body={grantedBodyTemplate} />
				{data.canManageAccess && <Column body={revokeBodyTemplate} />}
			</DataTable>
			{data.canManageSecrets && (
				<RecentChangesTable
					changes={data.recentAccessChanges ?? []}
					filterActions={[
						SecretAuditAction.AccessGranted,
						SecretAuditAction.AccessRevoked,
						SecretAuditAction.PasswordCheckFailed,
					]}
					downloadPath="/Secrets/AccessHistoryCsv"
					downloadFileName="secret-managers-history.csv"
				/>
			)}
			{isAddManagerFormOpen && (
				<AddManagerDialog
					availableSuperAdministrators={data.availableSuperAdministrators ?? []}
					onClose={() => setIsAddManagerFormOpen(false)}
				/>
			)}
			{isRevokingFormOpen && (
				<PasswordConfirmDialog
					title={`Revoke Access: ${isRevokingFormOpen.fullName || isRevokingFormOpen.userName}`}
					message={`${isRevokingFormOpen.fullName || isRevokingFormOpen.userName} will no longer be able to change secrets or decide who else can.`}
					confirmLabel="Revoke Access"
					onSubmit={(password) =>
						submitAndReset(
							setSecretAccess({
								userId: isRevokingFormOpen.id,
								canManageSecrets: false,
								password,
							}).unwrap(),
							reset
						)
					}
					onClose={() => setIsRevokingFormOpen(undefined)}
				/>
			)}
		</div>
	);
}
